import { StandardEvents } from '@shopify/events';

class MkVariantDropdown extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('keydown', this.onKeydown, { signal });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, { signal });
  }

  disconnectedCallback() { this.controller?.abort(); }
  get trigger() { return this.querySelector('[data-mk-trigger]'); }
  get list() { return this.querySelector('[role="listbox"]'); }
  get choices() { return [...this.querySelectorAll('[data-mk-choice]')]; }

  open(index) {
    document.querySelectorAll('mk-variant-dropdown').forEach((dropdown) => {
      if (dropdown !== this) dropdown.close();
    });
    this.trigger.setAttribute('aria-expanded', 'true');
    this.list.hidden = false;
    const selected = this.choices.findIndex((choice) => choice.getAttribute('aria-selected') === 'true');
    this.choices[index ?? Math.max(0, selected)]?.focus({ preventScroll: true });
  }

  close(restoreFocus = false) {
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.list) this.list.hidden = true;
    if (restoreFocus) this.trigger.focus({ preventScroll: true });
  }

  choose(choice) {
    const select = this.querySelector('select');
    if (select.value === choice.dataset.mkChoice) return this.close(true);
    select.value = choice.dataset.mkChoice;
    this.querySelector('[data-mk-value]').replaceChildren(...[...choice.querySelector('[data-mk-choice-content]').childNodes].map((node) => node.cloneNode(true)));
    this.choices.forEach((item) => item.setAttribute('aria-selected', String(item === choice)));
    this.close(true);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onClick = (event) => {
    const choice = event.target.closest('[data-mk-choice]');
    if (choice) return this.choose(choice);
    if (event.target.closest('[data-mk-trigger]')) {
      if (this.list.hidden) this.open();
      else this.close(true);
    }
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }
    if (event.key === 'Tab') return this.close();
    const choices = this.choices;
    const current = choices.indexOf(document.activeElement);
    let index;
    if (event.key === 'ArrowDown') index = current < 0 ? undefined : (current + 1) % choices.length;
    else if (event.key === 'ArrowUp') index = current < 0 ? choices.length - 1 : (current - 1 + choices.length) % choices.length;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = choices.length - 1;
    else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      this.search = now - (this.lastSearch ?? 0) < 600 ? (this.search ?? '') + event.key : event.key;
      this.lastSearch = now;
      index = choices.findIndex((choice) => choice.textContent.trim().toLocaleLowerCase().startsWith(this.search.toLocaleLowerCase()));
      if (index < 0) return;
    } else return;
    event.preventDefault();
    this.open(index);
  };
}

if (!customElements.get('mk-variant-dropdown')) customElements.define('mk-variant-dropdown', MkVariantDropdown);

const pendingSelections = new WeakMap();
document.addEventListener(StandardEvents.productSelect, (event) => {
  const picker = event.target.closest('variant-picker[data-template-product-match="true"]');
  if (!picker) return;
  pendingSelections.set(picker, event.promise);
  picker.setAttribute('aria-busy', 'true');
  event.promise.then(({ detail }) => {
    if (!detail?.html) return;
    for (const selector of ['.mk-product-inspirations-section', '.mk-product-story__visual']) {
      const current = document.querySelector(selector);
      const updated = detail.html.querySelector(selector);
      if (current && updated) current.replaceWith(updated);
    }
  }).catch((error) => {
    if (error.name !== 'AbortError') console.warn('[mk-variant-dropdown]', error);
  }).finally(() => {
    if (pendingSelections.get(picker) === event.promise) picker.removeAttribute('aria-busy');
  });
});
